import { ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { Location } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AError } from '../../models/aerror';
import { ItemData } from '../../models/item-data';

import { ToolbarService } from '../../services/toolbar.service';
import { TransportPlanData } from '../../models/transport-plan-data';
import { ScannerService } from '../../services/scanner.service';
import { MessageService } from '../../services/message.service';
import { TransportPlanService } from '../../services/transport-plan.service';
import {LocationAwareComponent} from '../location-aware/location-aware.component';

@Component({
  selector: 'app-transport-plan',
  templateUrl: './transport-plan.component.html',
  styleUrls: ['./transport-plan.component.scss']
})
export class TransportPlanComponent extends LocationAwareComponent implements OnInit, OnDestroy {

  // set class on host element
  @HostBinding('class') classes = 'app-content';

  itemData: ItemData;
  addressHtml: SafeHtml;
  transportPlanData: TransportPlanData;
  transportPlanHtml: SafeHtml;

  title = '';
  errorMessage: string = undefined;
  busy = false;

  private scanResultSubscription: Subscription;

  constructor(
    private messageService: MessageService,
    private transportPlanService: TransportPlanService,
    private ref: ChangeDetectorRef,
    private scanner: ScannerService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    private location: Location,
    public toolbar: ToolbarService) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.toolbar.setToolbarConfig({
      backAction: () => {
        this.location.back();
      },
      showRefresh: false,
      showSettings: true,
      showCall: true,
      showLogout: false
    });
    this.scanResultSubscription = this.scanner.scanResult.subscribe(scanResult => {
      this.scanner.pauseScanning();
      this.tryToFetchTransportPlan(scanResult.data).finally(() => {
        this.scanner.resumeScanning();
      });
    });
    this.title = this.translate.instant('transportPlan.title');
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.messageService.removeMultiple(this.messageService.warnings);
    this.scanResultSubscription?.unsubscribe();
  }

  goBack(): void {
    this.location.back();
  }

  // ===== Transport Plan operations ===== //

  private tryToFetchTransportPlan(barcode: string): Promise<void> {
    this.busy = true;
    return this.transportPlanService.scanItemTransportPlan(barcode)
      .then(res => {
        this.errorMessage = undefined;
        this.setResult(res[0], res[1]);
      })
      .catch(someError => {
        this.errorMessage = AError.fromSomeError(someError).humanMessage;
        this.clearResult();
      })
      .finally(() => {
        this.busy = false;
      });
  }

  private setResult(itemData: ItemData, transportPlanData: TransportPlanData): void {
    this.itemData = itemData;
    this.transportPlanData = transportPlanData;
    this.transportPlanHtml = this.sanitizer.bypassSecurityTrustHtml(this.transportPlanData.transportPlanAsHtml);
    this.addressHtml = this.buildAddressHtml(this.itemData);
    this.title = `${this.itemData.contractNumber}\t\t${this.itemData.itemNumber}`;
    this.ref.detectChanges();
  }

  private clearResult(): void {
    this.itemData = undefined;
    this.transportPlanData = undefined;
    this.transportPlanHtml = undefined;
    this.addressHtml = undefined;
    this.title = this.translate.instant('transportPlan.title');
    this.ref.detectChanges();
  }

  // ===== Other helpers ===== //

  private buildAddressHtml(itemData: ItemData): SafeHtml {
    if (!itemData || !itemData.to) {
      return undefined;
    }

    let html = '';
    if (itemData.to.name) {
      html += `${itemData.to.name}<br>`;
    }

    const address = itemData.to.address;
    if (address) {
      if (address.street) {
        html += `${address.street} `;
      }
      if (address.streetNumber) {
        html += `${address.streetNumber}`;
      }
      html += `<br>`;

      if (address.zipCode) {
        html += `${address.zipCode} `;
      }
      if (address.city) {
        html += `${address.city}`;
      }
      html += `<br>`;
    }
    return this.sanitizer.sanitize(SecurityContext.HTML, html);
  }
}
